import React, { useEffect, useRef } from "react";
import classNames from "classnames";
import {
    Combobox, ComboboxButton, ComboboxInput,
    ComboboxInputProps, ComboboxOption, ComboboxOptions
} from "@headlessui/react";
import InViewObserver from "@/component/InViewObserver";


interface BaseTypeaheadProps {
    className?: string;
    children: React.ReactNode;
}

interface SingleTypeaheadProps extends BaseTypeaheadProps {
    multiple?: false;
    selection?: string;
    onChange: (selected?: string) => void;
}

interface MultipleTypeaheadProps extends BaseTypeaheadProps {
    multiple: true;
    selection: string[];
    onChange: (selected: string[]) => void;
}

type TypeaheadProps = SingleTypeaheadProps | MultipleTypeaheadProps;

const Typeahead = React.forwardRef<HTMLDivElement, TypeaheadProps>((props, ref) => {
    const {
        multiple,
        selection,
        onChange,
        children,
        className
    } = props;

    const onSelect = (selected?: any) => {
        onChange(selected);
    };

    return (
        <Combobox as="div"
                  ref={ref}
                  multiple={multiple}
                  className={classNames("relative w-full", className)}
                  value={selection}
                  onChange={onSelect}
        >
            {children}
        </Combobox>
    );
});


export const TypeaheadInput: React.FC<ComboboxInputProps> = (props) => {
    const isComposingRef = useRef(false);
    const ref = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!ref.current) {
            return;
        }
        ref.current.value = props.defaultValue ?? "";
    }, [props.defaultValue]);

    return (
        <InViewObserver enabled={!!(ref.current && document.activeElement === ref.current)}
                        onHidden={() => ref.current?.blur()}
        >{inViewRef => (
            <ComboboxButton as="div"
                            ref={inViewRef}
            >
                <ComboboxInput ref={ref}
                               autoComplete="off"
                               className="dropdown-button placeholder:dropdown-button-placeholder input-default"
                               onKeyDown={e => {
                                   if (e.key === " ") {
                                       e.stopPropagation();
                                   }
                                   props.onKeyDown?.(e);
                               }}
                               {...props}
                               onCompositionStart={() => isComposingRef.current = true}
                               onCompositionEnd={() => isComposingRef.current = false}
                />
            </ComboboxButton>
        )}</InViewObserver>
    );
};

export const TypeaheadOptions: React.FC<{options: string[]}> = ({ options }) => {
    return (
        <ComboboxOptions anchor={{ to: "bottom", gap: 4 }}
                         className="w-[var(--input-width)] !max-h-60 bg-white border border-black-300 rounded-[6px] overflow-auto z-10 empty:border-none"
        >
            {options.map(option => (
                <ComboboxOption key={option}
                                value={option}
                                className="px-2 py-1 text-[14px] text-black-500 cursor-pointer data-[active]:bg-black-100 data-[selected]:bg-black-100"
                >
                    {option}
                </ComboboxOption>
            ))}
        </ComboboxOptions>
    );
};

export default Typeahead;
